import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import { WHATSAPP_URL, WA_CATERING } from "./Navbar";

const REAL_MIGNON =
  "https://customer-assets-lxgj4vgw.emergentagent.net/job_dolce-portfolio/artifacts/336e9bc090d589ca_IMG_9648.webp";
const REAL_TORTE =
  "https://customer-assets-lxgj4vgw.emergentagent.net/job_dolce-portfolio/artifacts/32a208d816efcf05_gpt-image-2.5-flare_inserisci_al_posto_della_brioche_del_cappuccino_e_della_spremuta_presenti_nell_i-2.webp";
const CENTRIFUGHE_IMG =
  "https://customer-assets-lxgj4vgw.emergentagent.net/job_dolce-portfolio/artifacts/4ec6f92d1fce8b32_gpt-image-2.5-sunburst_inserisci_al_posto_della_brioche_presente_nell_immagine_di_reference_le_tre_cent-1%20%281%29.webp";
const APERITIVI_IMG =
  "https://customer-assets-lxgj4vgw.emergentagent.net/job_dolce-portfolio/artifacts/07b3aebab3248e1f_gpt-image-2.5-sunburst_componi_la_foto_come_quella_di_reference_pero%CC%80_inserisci_al_posto_della_spremuta-1%20%281%29%20%281%29.webp";
const STOCK_COLAZIONE =
  "https://customer-assets-lxgj4vgw.emergentagent.net/job_dolce-portfolio/artifacts/27cd40601291ec39_gpt-image-2.5-sunburst_togli_la_torta_presente_nell_immagine_allegata_e_avvicina_il_cappucino_la_brioch-0%20%281%29.webp";

const fadeUp = {
  hidden: { opacity: 0, y: 36 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] },
  }),
};

const PhotoCard = ({ img, alt, badge, title, desc, className, i, tall, position = "center" }) => (
  <motion.article
    variants={fadeUp}
    custom={i}
    initial="hidden"
    whileInView="visible"
    viewport={{ once: true, margin: "-60px" }}
    className={`group relative rounded-3xl overflow-hidden ${className}`}
    data-testid={`product-card-${i}`}
  >
    <img
      src={img}
      alt={alt}
      loading="lazy"
      style={{ objectPosition: position }}
      className={`absolute inset-0 w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105`}
    />
    <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/20 to-transparent" />
    <div className="relative h-full flex flex-col justify-end p-6 sm:p-8">
      <span className="self-start text-[0.65rem] uppercase tracking-[0.2em] font-semibold bg-[#fbf6f1]/90 text-[#84232a] px-3 py-1.5 rounded-full mb-4">
        {badge}
      </span>
      <h3 className={`font-display ${tall ? "text-2xl sm:text-3xl" : "text-xl sm:text-2xl"} font-semibold text-[#fbf6f1]`}>
        {title}
      </h3>
      <p className="text-[#fbf6f1]/80 text-sm mt-2 max-w-sm leading-relaxed">{desc}</p>
    </div>
  </motion.article>
);

export const Prodotti = () => (
  <section id="prodotti" className="py-24 sm:py-32" data-testid="prodotti-section">
    <div className="max-w-7xl mx-auto px-5 sm:px-8">
      <div className="flex flex-wrap items-end justify-between gap-6 mb-14">
        <div>
          <motion.p
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="text-xs uppercase tracking-[0.3em] font-semibold text-[#84232a] mb-4"
            data-testid="prodotti-eyebrow"
          >
            L'assortimento
          </motion.p>
          <motion.h2
            variants={fadeUp}
            custom={1}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="font-display text-4xl sm:text-5xl font-semibold text-black leading-tight max-w-xl"
            data-testid="prodotti-title"
          >
            Ogni giorno,{" "}
            <span className="font-script text-[#84232a] font-normal">dal banco alla tavola.</span>
          </motion.h2>
        </div>
        <motion.p
          variants={fadeUp}
          custom={2}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="text-[#4a423d] text-sm sm:text-base max-w-sm leading-relaxed"
        >
          Dalla colazione all'aperitivo, dalle mignon alle grandi torte da cerimonia:
          tutto nasce nel nostro laboratorio, ogni mattina.
        </motion.p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-6 gap-4 sm:gap-5">
        <PhotoCard
          i={0}
          tall
          className="md:col-span-4 h-[380px] md:h-[440px]"
          img={REAL_MIGNON}
          alt="Vetrina di mignon e pasticceria artigianale"
          badge="Lavorazione giornaliera"
          title="Pasticceria Artigianale & Mignon"
          desc="Bignè, tartellette alla frutta, cannoli e paste fresche realizzate quotidianamente nel nostro laboratorio."
        />
        <PhotoCard
          i={1}
          className="md:col-span-2 h-[380px] md:h-[440px]"
          img={STOCK_COLAZIONE}
          alt="Croissant artigianale, cappuccino e spremuta d'arancia"
          position="35% center"
          badge="Ogni mattina"
          title="Colazioni & Caffetteria"
          desc="Espresso dall'aroma intenso, cappuccini cremosi e brioche a lenta lievitazione."
        />
        <PhotoCard
          i={2}
          className="md:col-span-2 h-[320px]"
          img={REAL_TORTE}
          alt="Torta al cioccolato, torta glassata rossa e monoporzione al passion fruit su alzatine"
          badge="Su misura"
          title="Torte su Ordinazione"
          desc="Creazioni personalizzate e cake design per compleanni, cerimonie ed eventi speciali."
        />
        <PhotoCard
          i={3}
          className="md:col-span-2 h-[320px]"
          img={APERITIVI_IMG}
          alt="Aperitivo con spritz, focaccia e sfoglie salate"
          badge="La sera"
          title="Aperitivi & Pasti Veloci"
          desc="Selezione di vini, cocktail e stuzzichini gourmet per la tua pausa nel centro di Trento."
        />
        <PhotoCard
          i={4}
          className="md:col-span-2 h-[320px]"
          img={CENTRIFUGHE_IMG}
          alt="Tre centrifughe colorate di frutta e verdura fresca"
          badge="100% naturale"
          title="Centrifughe & Merende"
          desc="Centrifughe di frutta e verdura di stagione, spremute naturali e merende pomeridiane."
        />
      </div>

      <motion.div
        variants={fadeUp}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        className="mt-5 flex flex-wrap items-center justify-between gap-6 bg-[#f4ede4] border border-[#e6dacb] rounded-3xl px-7 sm:px-10 py-7 sm:py-9"
        data-testid="catering-card"
      >
        <div className="max-w-xl">
          <span className="inline-block text-[0.65rem] uppercase tracking-[0.2em] font-semibold bg-[#84232a]/10 text-[#84232a] px-3 py-1.5 rounded-full mb-4">
            Su ordinazione
          </span>
          <h3 className="font-display text-2xl sm:text-3xl font-semibold text-black">
            Box Apericena
          </h3>
          <p className="text-[#4a423d] text-sm sm:text-base mt-2 leading-relaxed">
            <span className="font-display text-lg sm:text-xl text-black font-semibold block mb-1">
              Il tuo apericena, pronto da condividere.
            </span>
            Due box, una sola idea: rendere speciale il tuo apericena senza passare
            ore in cucina. 20 stuzzichini salati preparati interamente da noi,
            dall'impasto alla farcitura, racchiusi in una box pronta da portare in
            tavola. Scegli tra la versione classica e la Luxury, per un apericena
            ancora più ricercato.
          </p>
        </div>
        <div className="flex flex-wrap gap-3 w-full sm:w-auto sm:shrink-0">
          <Link
            to="/box"
            data-testid="catering-cta-scopri"
            className="group flex items-center gap-3 bg-[#84232a] text-[#fbf6f1] px-7 py-3.5 rounded-full text-sm font-medium hover:bg-[#63191f] transition-colors duration-300"
          >
            Scopri le box
            <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
          <a
            href={WA_CATERING}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="catering-cta-whatsapp"
            className="group flex items-center gap-3 border border-[#84232a] text-[#84232a] px-7 py-3.5 rounded-full text-sm font-medium hover:bg-[#84232a] hover:text-[#fbf6f1] transition-colors duration-300"
          >
            Ordina il tuo box
            <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:rotate-45" />
          </a>
        </div>
      </motion.div>

      <motion.a
        variants={fadeUp}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        href={WHATSAPP_URL}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="prodotti-cta-whatsapp"
        className="group mt-5 flex items-center justify-between gap-4 bg-[#84232a] text-[#fbf6f1] rounded-3xl px-7 sm:px-10 py-6 sm:py-8 hover:bg-[#63191f] transition-colors duration-300"
      >
        <div>
          <span className="font-script text-2xl sm:text-3xl block">Hai un evento in arrivo?</span>
          <span className="text-sm text-[#fbf6f1]/80 mt-1 block">
            Raccontaci la tua idea su WhatsApp: creiamo insieme la torta perfetta.
          </span>
        </div>
        <span className="shrink-0 w-12 h-12 rounded-full border border-[#fbf6f1]/40 flex items-center justify-center transition-transform duration-300 group-hover:rotate-45">
          <ArrowUpRight className="w-5 h-5" />
        </span>
      </motion.a>
    </div>
  </section>
);
